import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Paper,
  Stack,
  Typography,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import PublicShell from "./PublicShell";

type LocationState = {
  status?: string;
  header?: string;
};

const LoginErrorPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState) ?? {};
  const searchParams = new URLSearchParams(location.search);

  const header =
    state.header ||
    searchParams.get("header") ||
    "UNKNOWN_ERROR";
  const status =
    state.status ||
    searchParams.get("status") ||
    "error";

  const { message, reasons } = useMemo(() => {
    const defaultReasons = [
      "Brukeren er ikke korrekt registrert i Bluu.",
      "Det er ingen aktiv tenant eller database knyttet til denne brukeren.",
      "Kontakt Bluu dersom du mener dette er feil.",
    ];

    if (header && header !== "UNKNOWN_ERROR") {
      return {
        message: header,
        reasons: defaultReasons,
      };
    }

    return {
      message:
        "Det oppstod et problem ved innlogging. Du har ikke tilgang til Bluu akkurat nå.",
      reasons: defaultReasons,
    };
  }, [header]);

  return (
    <PublicShell>
      <Stack
        spacing={3}
        alignItems="center"
        justifyContent="center"
        sx={{ py: 6 }}
      >
        <Paper
          sx={{
            maxWidth: 640,
            width: "100%",
            p: 4,
            borderRadius: 3,
            background:
              "linear-gradient(180deg, rgba(55,71,79,0.85) 0%, rgba(38,50,56,0.98) 100%)",
            border: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Kunne ikke logge deg inn i Bluu
            </Typography>

            <Typography variant="overline" color="secondary.light">
              Status: {status}
            </Typography>
            {header && (
              <Typography variant="overline" color="secondary.light">
                Feilkode: {header}
              </Typography>
            )}

            <Typography variant="body1">{message}</Typography>

            <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 700 }}>
              Det kan blant annet skyldes:
            </Typography>
            <List dense>
              {reasons.map((r, idx) => (
                <ListItem key={idx} sx={{ py: 0 }}>
                  <ListItemText
                    primaryTypographyProps={{ variant: "body2" }}
                    primary={`• ${r}`}
                  />
                </ListItem>
              ))}
            </List>

            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate("/landing")}
              >
                Tilbake til forsiden
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => (window.location.href = "mailto:post@bluu.no")}
              >
                Kontakt Bluu
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </PublicShell>
  );
};

export default LoginErrorPage;
